import { useState } from 'react';
import { User, Building2, ChevronLeft, Settings } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAssistants, useCreateAssistant } from '../../hooks/useAssistants';
import { useSportTypes } from '../../hooks/useSportTypes';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';

export function DirectionSwitcherSheet() {
  const user = useAuthStore((s) => s.user);
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const selectedOrganizationId = useAuthStore((s) => s.selectedOrganizationId);
  const syncCurrentAssistant = useAuthStore((s) => s.syncCurrentAssistant);

  const closeDirectionSwitcher = useTabNavigationStore((s) => s.closeDirectionSwitcher);
  const pushScreen = useTabNavigationStore((s) => s.pushScreen);
  const switchTab = useTabNavigationStore((s) => s.switchTab);

  const { data: assistants, isLoading, error, refetch } = useAssistants(selectedOrganizationId || '');
  const { data: sportTypes } = useSportTypes();
  const createAssistant = useCreateAssistant();
  const { maxAssistants } = useSubscriptionLimits();

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [sportSearch, setSportSearch] = useState('');

  const currentCount = assistants?.length ?? 0;
  const canCreateMore = maxAssistants === -1 || currentCount < maxAssistants;

  const handleSelect = async (assistantId: string) => {
    try {
      await syncCurrentAssistant(assistantId);
      closeDirectionSwitcher();
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch direction:', err);
    }
  };

  const handleOpenDetail = (assistantId: string) => {
    closeDirectionSwitcher();
    switchTab('home');
    pushScreen('assistant-detail', { assistantId });
  };

  const handleAddDirection = () => {
    if (!canCreateMore) {
      alert(`Достигнут лимит направлений (${maxAssistants}). Обновите тариф для добавления.`);
      return;
    }
    setShowForm(true);
  };

  const handleCreate = async () => {
    // Use selectedSport if chosen from list, otherwise use sportSearch (custom sport)
    const finalSportName = selectedSport || sportSearch.trim();

    if (!name.trim() || !finalSportName || !user || !selectedOrganizationId) return;

    // sport_type берётся из БД (API), fallback на 'individual' для пользовательских видов спорта
    const matchedSport = sportTypes?.find(s => s.name === finalSportName);
    const sportType = matchedSport?.sport_type || 'individual';

    try {
      const created = await createAssistant.mutateAsync({
        user_id: user.id,
        organization_id: selectedOrganizationId,
        name: name.trim(),
        sport_type: sportType,
        sport_name: finalSportName,
      });
      // Switch to newly created assistant
      await syncCurrentAssistant(created.id);
      closeDirectionSwitcher();
      window.location.reload();
    } catch (err) {
      console.error('Failed to create direction:', err);
      alert('Не удалось создать направление. Попробуйте еще раз.');
    }
  };

  const handleBackToList = () => {
    setShowForm(false);
    setName('');
    setSelectedSport('');
    setSportSearch('');
  };

  // Фильтрация видов спорта по поиску
  const filteredSports = sportTypes?.filter((s) =>
    s.name.toLowerCase().includes(sportSearch.toLowerCase())
  ) || [];

  // ── Форма создания ──────────────────────────────────────────────────────
  if (showForm) {
    return (
      <div className="py-4">
        <div className="flex items-center gap-2 mb-4">
          <button onClick={handleBackToList} className="p-1">
            <ChevronLeft size={24} style={{ color: 'var(--accent)' }} />
          </button>
          <h2
            className="text-xl leading-6 font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            Новое направление
          </h2>
        </div>

        <div className="space-y-4">
          {/* Название */}
          <div>
            <label
              className="block text-[13px] font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Название *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Фитнес"
              disabled={createAssistant.isPending}
              className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          {/* Вид спорта */}
          <div>
            <label
              className="block text-[13px] font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              Вид спорта *
            </label>

            {/* Поиск */}
            <input
              type="text"
              value={sportSearch}
              onChange={(e) => setSportSearch(e.target.value)}
              placeholder="Поиск вида спорта..."
              disabled={createAssistant.isPending}
              className="w-full h-11 px-3 rounded-xl border-none outline-none text-[15px] mb-2"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />

            {/* Список видов спорта */}
            <div
              className="max-h-[200px] overflow-y-auto rounded-xl"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              {filteredSports.length > 0 ? (
                filteredSports.map((sport) => {
                  const isSelected = selectedSport === sport.name;
                  return (
                    <button
                      key={sport.code}
                      onClick={() => setSelectedSport(sport.name)}
                      disabled={createAssistant.isPending}
                      className="w-full text-left px-3 py-2.5 flex items-center justify-between transition-opacity disabled:opacity-40"
                      style={{
                        borderBottom: '1px solid var(--separator)',
                        color: 'var(--text-primary)',
                        backgroundColor: isSelected ? 'var(--accent-light)' : 'transparent',
                      }}
                    >
                      <span className="text-[15px]">{sport.name}</span>
                      {isSelected && (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="var(--accent)"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  );
                })
              ) : sportSearch.trim() ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-[13px] mb-1" style={{ color: 'var(--text-hint)' }}>
                    Вид спорта не найден
                  </p>
                  <p className="text-[13px]" style={{ color: 'var(--accent)' }}>
                    Будет создан новый: "{sportSearch.trim()}"
                  </p>
                </div>
              ) : (
                <div className="px-3 py-4 text-center">
                  <p className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
                    Введите название вида спорта
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Кнопка создания */}
          <div className="pt-2">
            <Button
              variant="primary"
              fullWidth
              onClick={handleCreate}
              disabled={!name.trim() || (!selectedSport && !sportSearch.trim())}
              loading={createAssistant.isPending}
            >
              Создать
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Список направлений ──────────────────────────────────────────────────
  return (
    <div className="py-4">
      <h2
        className="text-xl leading-6 font-semibold mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        Направления
      </h2>

      {isLoading ? (
        <div className="py-8 text-center">
          <p className="text-[15px]" style={{ color: 'var(--text-hint)' }}>
            Загрузка...
          </p>
        </div>
      ) : error ? (
        <div className="py-8 text-center">
          <p className="text-[15px] mb-2" style={{ color: 'var(--text-hint)' }}>
            Не удалось загрузить направления
          </p>
          <button
            onClick={() => refetch()}
            className="text-[15px] font-medium"
            style={{ color: 'var(--accent)' }}
          >
            Повторить
          </button>
        </div>
      ) : !assistants || assistants.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-[15px] mb-2" style={{ color: 'var(--text-hint)' }}>
            У вас пока нет направлений
          </p>
          <p className="text-[13px]" style={{ color: 'var(--text-hint)' }}>
            Создайте первое направление (вид спорта)
          </p>
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {assistants.map((assistant) => {
            const isActive = assistant.id === currentAssistantId;
            const isOrganization = !!assistant.organization_id;
            const Icon = isOrganization ? Building2 : User;

            return (
              <div
                key={assistant.id}
                className="rounded-xl flex items-center gap-0"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                {/* Main clickable area — select direction */}
                <button
                  className="flex-1 p-4 flex items-center gap-3 active:opacity-60 transition-opacity min-w-0"
                  onClick={() => handleSelect(assistant.id)}
                >
                  {/* Icon indicator */}
                  <div
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'var(--accent-light)' }}
                  >
                    <Icon size={20} style={{ color: 'var(--accent)' }} />
                  </div>

                  {/* Text content */}
                  <div className="flex-1 text-left min-w-0">
                    <p
                      className="text-[17px] leading-[22px] font-semibold truncate"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {assistant.name}
                    </p>
                    {assistant.sport_name && (
                      <p
                        className="text-[15px] leading-5 truncate"
                        style={{ color: 'var(--text-hint)' }}
                      >
                        {assistant.sport_name}
                      </p>
                    )}
                  </div>

                  {/* Active checkmark */}
                  {isActive && (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--accent)"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>

                {/* Settings button — edit/delete direction */}
                <button
                  className="flex-shrink-0 p-4 active:opacity-60 transition-opacity"
                  onClick={() => handleOpenDetail(assistant.id)}
                  title="Настройки направления"
                >
                  <Settings size={20} style={{ color: 'var(--text-hint)' }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Button onClick={handleAddDirection} fullWidth>
        <span className="flex items-center justify-center gap-2">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>Добавить направление</span>
        </span>
      </Button>
    </div>
  );
}
